import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

// Functional component
function TotalClientReportOverview({ trackReport, expenses }) {
  const [displayedCount, setDisplayedCount] = useState(0);
  const [targetCount, setTargetCount] = useState(0);
  const animationFrameRef = useRef(null);
  const reportingFinancialOverview = useSelector((state) => state.reportingFinancialOverview);

  const clientsCount = useMemo(() => {
    if (!trackReport || !expenses || !reportingFinancialOverview) {
      return 0;
    }

    try {
      const financialOverviewYear = reportingFinancialOverview.financialOverviewYear;

      const filteredClients = trackReport.filter((entry) => {
        if (!entry || !entry.startTime) return false;
        const startTimeYear = new Date(entry.startTime).getFullYear();
        return startTimeYear === financialOverviewYear;
      });

      const uniqueClients = [];
      filteredClients.forEach((entry) => {
        const client = entry.clientId;
        if (
          client &&
          !client.is_delete &&
          (reportingFinancialOverview.financialOverviewClient &&
            reportingFinancialOverview.financialOverviewClient.includes(client._id)) &&
          (reportingFinancialOverview.financialOverviewCurrency &&
            reportingFinancialOverview.financialOverviewCurrency.includes(client?.preferred_currency || 'USD')) &&
          !uniqueClients.some((c) => c._id === client._id)
        ) {
          uniqueClients.push(client);
        }
      });

      const expenseClients = expenses
        .filter(
          (expense) =>
            expense &&
            expense.exp_date &&
            new Date(expense.exp_date).getFullYear() === financialOverviewYear &&
            expense.exp_client &&
            !expense.exp_client.is_delete &&
            (reportingFinancialOverview.financialOverviewClient &&
              reportingFinancialOverview.financialOverviewClient.includes(expense.exp_client._id)) &&
            (reportingFinancialOverview.financialOverviewCurrency &&
              reportingFinancialOverview.financialOverviewCurrency.includes(expense.exp_client?.preferred_currency || 'USD'))
        )
        .map((expense) => expense.exp_client);

      expenseClients.forEach((client) => {
        if (client && !uniqueClients.some((c) => c._id === client._id)) {
          uniqueClients.push(client);
        }
      });

      return uniqueClients.length;
    } catch (error) {
      console.error('Error calculating client count:', error);
      return 0;
    }
  }, [trackReport, expenses, reportingFinancialOverview]);

  useEffect(() => {
    setTargetCount(clientsCount);
  }, [clientsCount]);

  useEffect(() => {
    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const startValue = displayedCount;
    const endValue = targetCount;
    const duration = 1000; // Animation duration in milliseconds
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      const currentValue = startValue + (endValue - startValue) * easeOut;
      setDisplayedCount(Math.floor(currentValue));

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayedCount(endValue);
        animationFrameRef.current = null;
      }
    };

    if (Math.abs(startValue - endValue) > 0.01) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [targetCount]);

  return (
    <div>
      <div className="overview-item">
        <div className="overview-title-outer">
          <div className="overview-title">
            Total Clients
          </div>
          <div className="overview-icon">
            <svg width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g filter="url(#filter0_d_2951_62)">
                <circle cx="24.5" cy="24.5" r="20.5" fill="white" />
              </g>
              <path d="M31.3956 12.1001H19.8955C17.8506 12.1001 16.1068 13.4063 15.4597 15.2251H13.6228C13.3343 15.2251 13.1001 15.4584 13.1001 15.7459C13.1001 16.0334 13.3343 16.2668 13.6228 16.2668H15.2203C15.2015 16.4376 15.191 16.6116 15.191 16.7876V19.3918H13.6228C13.3343 19.3918 13.1001 19.6251 13.1001 19.9126C13.1001 20.2001 13.3343 20.4334 13.6228 20.4334H15.191V24.0793H13.6228C13.3343 24.0793 13.1001 24.3126 13.1001 24.6001C13.1001 24.8876 13.3343 25.1209 13.6228 25.1209H15.191V28.7668H13.6228C13.3343 28.7668 13.1001 29.0001 13.1001 29.2876C13.1001 29.5751 13.3343 29.8084 13.6228 29.8084H15.191V32.4126C15.191 32.5886 15.2015 32.7626 15.2203 32.9334H13.6228C13.3343 32.9334 13.1001 33.1668 13.1001 33.4543C13.1001 33.7418 13.3343 33.9751 13.6228 33.9751H15.4597C16.1068 35.7938 17.8506 37.1001 19.8955 37.1001H31.3956C33.9893 37.1001 36.1001 34.997 36.1001 32.4126V16.7876C36.1001 14.2032 33.9893 12.1001 31.3956 12.1001ZM35.0546 32.4126C35.0546 34.423 33.4133 36.0584 31.3956 36.0584H19.8955C17.8778 36.0584 16.2365 34.423 16.2365 32.4126V16.7876C16.2365 14.7772 17.8778 13.1418 19.8955 13.1418H31.3956C33.4133 13.1418 35.0546 14.7772 35.0546 16.7876V32.4126ZM25.6456 24.6001C27.3747 24.6001 28.7819 23.198 28.7819 21.4751C28.7819 19.7522 27.3747 18.3501 25.6456 18.3501C23.9164 18.3501 22.5092 19.7522 22.5092 21.4751C22.5092 23.198 23.9164 24.6001 25.6456 24.6001ZM25.6456 19.3918C26.7987 19.3918 27.7365 20.3261 27.7365 21.4751C27.7365 22.6241 26.7987 23.5584 25.6456 23.5584C24.4924 23.5584 23.5546 22.6241 23.5546 21.4751C23.5546 20.3261 24.4924 19.3918 25.6456 19.3918ZM30.8728 30.8501V31.3709C30.8728 31.6584 30.6386 31.8918 30.3501 31.8918C30.0616 31.8918 29.8274 31.6584 29.8274 31.3709V30.8501C29.8274 28.5522 27.9518 26.6834 25.6456 26.6834C23.3393 26.6834 21.4637 28.5522 21.4637 30.8501V31.3709C21.4637 31.6584 21.2295 31.8918 20.941 31.8918C20.6525 31.8918 20.4183 31.6584 20.4183 31.3709V30.8501C20.4183 27.9782 22.7632 25.6418 25.6456 25.6418C28.5279 25.6418 30.8728 27.9782 30.8728 30.8501Z" fill="#6A7988" />
              <path fillRule="evenodd" clipRule="evenodd" d="M19.8955 12C17.832 12 16.0689 13.3023 15.3896 15.125H13.6227C13.2793 15.125 13 15.4028 13 15.7458C13 16.0889 13.2793 16.3667 13.6227 16.3667H15.1098C15.0976 16.5051 15.0909 16.6456 15.0909 16.7875V19.2917H13.6227C13.2793 19.2917 13 19.5694 13 19.9125C13 20.2556 13.2793 20.5333 13.6227 20.5333H15.0909V23.9792H13.6227C13.2793 23.9792 13 24.2569 13 24.6C13 24.9431 13.2793 25.2208 13.6227 25.2208H15.0909V28.6667H13.6227C13.2793 28.6667 13 28.9444 13 29.2875C13 29.6306 13.2793 29.9083 13.6227 29.9083H15.0909V32.4125C15.0909 32.5544 15.0976 32.6949 15.1098 32.8333H13.6227C13.2793 32.8333 13 33.1111 13 33.4542C13 33.7972 13.2793 34.075 13.6227 34.075H15.3896C16.0689 35.8977 17.832 37.2 19.8955 37.2H31.3955C34.0441 37.2 36.2 35.0524 36.2 32.4125V16.7875C36.2 14.1475 34.0441 12 31.3955 12H19.8955ZM15.5538 15.2585C16.1872 13.4784 17.8941 12.2 19.8955 12.2H31.3955C33.9343 12.2 36 14.2587 36 16.7875V32.4125C36 34.9413 33.9343 37 31.3955 37H19.8955C17.8941 37 16.1872 35.7215 15.5538 33.9415L15.5301 33.875H13.6227C13.3891 33.875 13.2 33.6861 13.2 33.4542C13.2 33.2222 13.3891 33.0333 13.6227 33.0333H15.3318L15.3196 32.9224C15.3011 32.7549 15.2909 32.5846 15.2909 32.4125V29.7083H13.6227C13.3891 29.7083 13.2 29.5194 13.2 29.2875C13.2 29.0556 13.3891 28.8667 13.6227 28.8667H15.2909V25.0208H13.6227C13.3891 25.0208 13.2 24.8319 13.2 24.6C13.2 24.3681 13.3891 24.1792 13.6227 24.1792H15.2909V20.3333H13.6227C13.3891 20.3333 13.2 20.1444 13.2 19.9125C13.2 19.6806 13.3891 19.4917 13.6227 19.4917H15.2909V16.7875C15.2909 16.6154 15.3011 16.4451 15.3196 16.2776L15.3318 16.1667H13.6227C13.3891 16.1667 13.2 15.9778 13.2 15.7458C13.2 15.5139 13.3891 15.325 13.6227 15.325H15.5301L15.5538 15.2585ZM16.3364 16.7875C16.3364 14.8326 17.9326 13.2417 19.8955 13.2417H31.3955C33.3583 13.2417 34.9545 14.8326 34.9545 16.7875V32.4125C34.9545 34.3673 33.3583 35.9583 31.3955 35.9583H19.8955C17.9326 35.9583 16.3364 34.3673 16.3364 32.4125V16.7875ZM19.8955 13.0417C17.8228 13.0417 16.1364 14.7215 16.1364 16.7875V32.4125C16.1364 34.4785 17.8228 36.1583 19.8955 36.1583H31.3955C33.4681 36.1583 35.1545 34.4785 35.1545 32.4125V16.7875C35.1545 14.7215 33.4681 13.0417 31.3955 13.0417H19.8955ZM25.6455 18.25C23.8614 18.25 22.4091 19.6965 22.4091 21.475C22.4091 23.2535 23.8614 24.7 25.6455 24.7C27.4295 24.7 28.8818 23.2535 28.8818 21.475C28.8818 19.6965 27.4295 18.25 25.6455 18.25ZM22.6091 21.475C22.6091 19.8077 23.9712 18.45 25.6455 18.45C27.3197 18.45 28.6818 19.8077 28.6818 21.475C28.6818 23.1423 27.3197 24.5 25.6455 24.5C23.9712 24.5 22.6091 23.1423 22.6091 21.475ZM23.6545 21.475C23.6545 20.3816 24.5472 19.4917 25.6455 19.4917C26.7437 19.4917 27.6364 20.3816 27.6364 21.475C27.6364 22.5684 26.7437 23.4583 25.6455 23.4583C24.5472 23.4583 23.6545 22.5684 23.6545 21.475ZM25.6455 19.2917C24.4374 19.2917 23.4545 20.2705 23.4545 21.475C23.4545 22.6795 24.4374 23.6583 25.6455 23.6583C26.8535 23.6583 27.8364 22.6795 27.8364 21.475C27.8364 20.2705 26.8535 19.2917 25.6455 19.2917ZM20.5182 30.85C20.5182 28.0337 22.818 25.7417 25.6455 25.7417C28.4729 25.7417 30.7727 28.0337 30.7727 30.85V31.3708C30.7727 31.4582 30.7459 31.5395 30.7 31.6068C30.7 31.6046 30.7 31.6023 30.7 31.6C30.7 31.2686 30.4314 31 30.1 31H29.9273V30.85C29.9273 28.4965 28.0066 26.5833 25.6455 26.5833C23.2843 26.5833 21.3636 28.4965 21.3636 30.85V31H21.1C20.8259 31 20.5948 31.1838 20.523 31.4348C20.5198 31.4139 20.5182 31.3926 20.5182 31.3708V30.85ZM20.5601 31.862C20.413 31.7484 20.3182 31.5707 20.3182 31.3708V30.85C20.3182 27.9226 22.7083 25.5417 25.6455 25.5417C28.5827 25.5417 30.9727 27.9226 30.9727 30.85V31.3708C30.9727 31.628 30.8158 31.8485 30.5925 31.9428C30.4841 32.0983 30.3039 32.2 30.1 32.2H21.1C20.8626 32.2 20.6573 32.0621 20.5601 31.862ZM21.5636 31H29.7273V30.85C29.7273 28.6077 27.8968 26.7833 25.6455 26.7833C23.3941 26.7833 21.5636 28.6077 21.5636 30.85V31Z" fill="#6A7988" />
              <defs>
                <filter id="filter0_d_2951_62" x="0" y="0" width="49" height="49" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0.468733 0 0 0 0 0.51145 0 0 0 0 0.554167 0 0 0 0.39 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2951_62" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2951_62" result="shape" />
                </filter>
              </defs>
            </svg>
          </div>
        </div>

        <div className="overview-rice">
          {displayedCount}
        </div>
      </div>
    </div>
  );
}

export default TotalClientReportOverview;
